import { Dialog, DialogTrigger, Popover } from 'react-aria-components';

import { StackingLayer, useStackingZIndex } from '@/contexts/StackingContext';
import { Button } from '@/lib/base/buttons/button';
import { cx } from '@/utils/cx';

import { calendarDayKey } from '../utils/calendarDays';
import { isEventPast } from '../utils/calendarUtils';
import type { CalendarEvent } from '../utils/eventMapper';
import { MonthEventChip } from './MonthEventChip';
import { MonthInlineEventRow } from './MonthInlineEventRow';

/** Max all-day lanes/rows shown per day before the rest collapse into a "+N more" popover. */
export const MAX_VISIBLE_ALL_DAY_LANES = 3;

/**
 * Compute the chip's `spanPosition` for an all-day event RELATIVE to a
 * given day. Mirrors the calendar's per-row span logic so the popover
 * uses the same continuation-edge cues:
 *  - 'start'  → event starts in this day, extends to the next day
 *  - 'end'    → event started before this day, ends in this day
 *  - 'middle' → event spans through (started before, extends after)
 *  - undefined → single-day event (no continuation)
 *
 * All-day events have `start_time = '<startDate>T00:00:00'` and
 * `end_time = '<endDate>T23:59:59'`, so we just compare the YMD
 * prefixes against the popover day's profile-timezone YYYY-MM-DD.
 */
function spanPositionForDay(
  event: CalendarEvent,
  dayYmd: string
): 'start' | 'middle' | 'end' | undefined {
  const startYmd = event.start_time.split('T')[0];
  const endYmd = event.end_time.split('T')[0];
  const startsBefore = startYmd < dayYmd;
  const endsAfter = endYmd > dayYmd;
  if (startsBefore && endsAfter) {
    return 'middle';
  }
  if (startsBefore) {
    return 'end';
  }
  if (endsAfter) {
    return 'start';
  }
  return undefined;
}

/**
 * Day-detail popover content. Lists every event for the day:
 *  - All-day events as `MonthEventChip` (full-width colored bar) with
 *    chevron continuation cues when the event extends past the day.
 *  - Timed events as `MonthInlineEventRow` (dot + time + title).
 * Rendered inside a react-aria Popover/Dialog anchored to the "+N more"
 * trigger.
 */
export function DayEventsPopover({
  date,
  timezone,
  events,
  onEventClick,
}: {
  /** The calendar day this popover lists (profile-tz midnight) */
  date: Date;
  /** IANA timezone (profile timezone) `date` is a calendar day in */
  timezone: string;
  events: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
}) {
  const zIndex = useStackingZIndex();
  const fullDateLabel = date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: timezone,
  });
  const dayYmd = calendarDayKey(date, timezone);
  return (
    <Popover
      placement='right top'
      offset={4}
      shouldFlip
      containerPadding={16}
      className={({ isEntering, isExiting }) =>
        cx(
          'origin-(--trigger-anchor-point) will-change-transform',
          isEntering &&
            'duration-150 ease-out animate-in fade-in placement-right:slide-in-from-left-0.5 placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5',
          isExiting &&
            'duration-100 ease-in animate-out fade-out placement-right:slide-out-to-left-0.5 placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5'
        )
      }
      style={{ zIndex }}
    >
      <StackingLayer>
        <Dialog
          aria-label={`Events for ${fullDateLabel}`}
          className='min-w-64 max-w-72 max-h-80 overflow-y-auto rounded-lg bg-primary shadow-xl ring-1 ring-secondary_alt outline-hidden'
        >
          <div className='border-b border-secondary px-3 py-2'>
            <h3 className='text-sm font-semibold text-primary'>
              {fullDateLabel}
            </h3>
            <p className='text-xs text-tertiary'>
              {events.length} event{events.length === 1 ? '' : 's'}
            </p>
          </div>
          <div className='space-y-0.5 p-2'>
            {events.map(event => {
              if (event.is_all_day) {
                const spanPosition = spanPositionForDay(event, dayYmd);
                return (
                  <MonthEventChip
                    key={event.id}
                    timezone={timezone}
                    event={event}
                    onClick={onEventClick}
                    isPast={isEventPast(event)}
                    isMultiDay={spanPosition !== undefined}
                    spanPosition={spanPosition}
                  />
                );
              }
              return (
                <MonthInlineEventRow
                  key={event.id}
                  timezone={timezone}
                  event={event}
                  onClick={onEventClick}
                  isPast={isEventPast(event)}
                />
              );
            })}
          </div>
        </Dialog>
      </StackingLayer>
    </Popover>
  );
}

/**
 * Self-contained "+N more" trigger that opens `DayEventsPopover`. Used by
 * WeekView and DayView. Safe to place inside a clickable container — the
 * button stops propagation before the click can bubble up.
 */
export function MoreEventsPopover({
  date,
  timezone,
  events,
  count,
  onEventClick,
  className,
}: {
  /** The calendar day this trigger belongs to (profile-tz midnight) */
  date: Date;
  /** IANA timezone (profile timezone) `date` is a calendar day in */
  timezone: string;
  /** Full event list for the day (all-day + timed), in display order. */
  events: CalendarEvent[];
  /** The N shown in "+N more". */
  count: number;
  onEventClick?: (event: CalendarEvent) => void;
  /** Extra classes for the trigger button (placement per view). */
  className?: string;
}) {
  return (
    <DialogTrigger>
      <Button
        color='tertiary'
        size='sm'
        className={cx(
          '!justify-start !px-1.5 !py-0.5 !text-xs !font-medium',
          className
        )}
        aria-label={`Show ${count} more events for ${date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', timeZone: timezone })}`}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        +{count} more
      </Button>
      <DayEventsPopover
        date={date}
        timezone={timezone}
        events={events}
        onEventClick={onEventClick}
      />
    </DialogTrigger>
  );
}
